import type { AiReviewJob } from '@zunelo/shared';

export type AiReviewProgressKind =
  | 'queued'
  | 'fetching'
  | 'reviewing'
  | 'retrying'
  | 'finalizing'
  | 'complete'
  | 'error'
  | 'cancelled';

export type AiReviewStatusErrorCode =
  'AI_REVIEW_SYNC_UNAVAILABLE' | 'AI_REVIEW_STATUS_REFRESH_FAILED';

export interface AiReviewStatusRefreshError {
  code: AiReviewStatusErrorCode;
  message: string;
}

export interface AiReviewProgress {
  kind: AiReviewProgressKind;
  message: string;
  completedBatches?: number;
  totalBatches?: number;
  findingCount?: number;
  attempt?: number;
  updatedAt: string;
  canRetry: boolean;
  statusRefreshError?: AiReviewStatusRefreshError;
}

const MAX_REVIEW_ATTEMPTS = 2;

function safeBatchCount(value: number | undefined, total: number | undefined): number {
  const normalized = Number.isFinite(value) ? Math.max(0, Math.trunc(value ?? 0)) : 0;
  return total === undefined ? normalized : Math.min(normalized, Math.max(0, Math.trunc(total)));
}

function findingCount(job: AiReviewJob): number | undefined {
  return job.checkpoint ? job.checkpoint.findings.length : undefined;
}

function reviewMessage(job: AiReviewJob): string {
  const checkpoint = job.checkpoint;
  if (!checkpoint || checkpoint.batchesTotal === undefined) {
    return 'Reviewing the diff and looking for actionable findings.';
  }
  const total = Math.max(0, Math.trunc(checkpoint.batchesTotal));
  const completed = safeBatchCount(checkpoint.batchesCompleted, total);
  const current = total === 0 ? 0 : Math.min(Math.max(completed + 1, 1), total);
  const findings = checkpoint.findings.length;
  return `Reviewing batch ${current} of ${total} · ${findings} ${findings === 1 ? 'finding' : 'findings'} collected.`;
}

function baseProgress(job: AiReviewJob): AiReviewProgress {
  const completedBatches = job.checkpoint
    ? safeBatchCount(job.checkpoint.batchesCompleted, job.checkpoint.batchesTotal)
    : undefined;
  const totalBatches = job.checkpoint?.batchesTotal;
  const findingCountValue = findingCount(job);
  return {
    kind: 'queued',
    message: 'Review queued.',
    ...(completedBatches === undefined ? {} : { completedBatches }),
    ...(totalBatches === undefined ? {} : { totalBatches }),
    ...(findingCountValue === undefined ? {} : { findingCount: findingCountValue }),
    ...(job.attempts === undefined ? {} : { attempt: job.attempts }),
    updatedAt: job.updatedAt,
    canRetry: false,
  };
}

export function projectAiReviewProgress(
  job: AiReviewJob,
  statusRefreshError?: AiReviewStatusRefreshError,
): AiReviewProgress {
  const progress = baseProgress(job);
  if (job.status === 'completed') {
    progress.kind = 'complete';
    progress.message = 'Review complete.';
  } else if (job.status === 'failed') {
    progress.kind = 'error';
    progress.message = job.errorMessage?.trim() || 'The review failed. Try again.';
    progress.canRetry = true;
  } else if (job.status === 'cancelled') {
    progress.kind = 'cancelled';
    progress.message = 'Review cancelled.';
    progress.canRetry = true;
  } else if (job.status === 'superseded') {
    progress.kind = 'cancelled';
    progress.message = 'Review superseded by a newer run.';
    progress.canRetry = true;
  } else if (job.status === 'retrying') {
    progress.kind = 'retrying';
    progress.message = `Provider unavailable · retry ${Math.min(job.attempts ?? 1, MAX_REVIEW_ATTEMPTS)} of ${MAX_REVIEW_ATTEMPTS} scheduled.`;
  } else if (job.stage === 'fetching_diff') {
    progress.kind = 'fetching';
    progress.message = 'Preparing the pull request diff.';
  } else if (job.stage === 'reviewing') {
    progress.kind = 'reviewing';
    progress.message = reviewMessage(job);
  } else if (job.stage === 'finalizing') {
    progress.kind = 'finalizing';
    progress.message = 'Synthesizing and verifying findings.';
  }

  if (statusRefreshError) {
    progress.statusRefreshError = statusRefreshError;
    progress.canRetry = true;
  }
  return progress;
}
